import type { TovProfileResponse } from "contracts/api/client-tov/responses";
import type { TovVersionSummaryResponse } from "contracts/api/client-tov/version.responses";
import { createSignal, For, Show } from "solid-js";
import { Field } from "./tovField";

// Version history selector. Lists every Version of the selected profile (draft,
// active, archived) so the operator can open a prior Version read-only to compare.
export function VersionSwitcher(props: {
  versions: TovVersionSummaryResponse[];
  activeVersionId: string | null;
  onSelect: (versionId: string) => void;
}) {
  return (
    <Show when={props.versions.length > 0}>
      <div class="tov-version-switcher">
        <span class="tov-field-label">Versions</span>
        <div class="tov-profile-chips">
          <For each={props.versions}>
            {(version) => (
              <button
                type="button"
                class={`tov-profile-chip ${
                  version.id === props.activeVersionId ? "is-active" : ""
                }`}
                onClick={() => props.onSelect(version.id)}
              >
                {version.label} · {version.status}
              </button>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
}

// Slice-1 author tooling, restyled. Profile selection + create profile/version
// live in ProfileBar; trait authoring in AddTraitForm. Other definition types
// are seed/API-authored, so they have no inline form yet.
export function ProfileBar(props: {
  profiles: TovProfileResponse[];
  activeVersionId: string | null;
  onSelect: (profile: TovProfileResponse) => void;
  onCreateProfile: (name: string) => void;
  onCreateVersion: (profileId: string, label: string, summary: string) => void;
}) {
  const [open, setOpen] = createSignal(false);
  // First-run onboarding: with no profile yet, the create form IS the page —
  // hiding it behind the "Author" toggle made the required first step
  // invisible (E2E finding F3).
  return (
    <Show
      when={props.profiles.length > 0}
      fallback={
        <div class="tov-profilebar">
          <div class="tov-panel">
            <span class="tov-panel-title">Create your first brand profile</span>
            <p class="tov-sub">
              No brand profile exists yet. A profile holds this client's
              versioned Tone of Voice — create one, then add a draft version and
              upload your brand guidelines.
            </p>
            <CreateProfile onCreate={props.onCreateProfile} />
          </div>
        </div>
      }
    >
      <div class="tov-profilebar">
        <div class="tov-profilebar-row">
          <div class="tov-profile-chips">
            <For each={props.profiles}>
              {(profile) => (
                <button
                  type="button"
                  class={`tov-profile-chip ${
                    profile.currentVersionId === props.activeVersionId
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() => props.onSelect(profile)}
                >
                  {profile.name}
                </button>
              )}
            </For>
          </div>
          <button type="button" class="btn" onClick={() => setOpen(!open())}>
            {open() ? "Close authoring" : "Author"}
          </button>
        </div>
        <Show when={open()}>
          <div class="tov-panel">
            <span class="tov-panel-title">Create profile</span>
            <CreateProfile onCreate={props.onCreateProfile} />
            <For each={props.profiles}>
              {(profile) => (
                <>
                  <span class="tov-panel-title">
                    New draft for {profile.name}
                  </span>
                  <CreateVersion
                    onCreate={(label, summary) =>
                      props.onCreateVersion(profile.id, label, summary)
                    }
                  />
                </>
              )}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
}

function CreateProfile(props: { onCreate: (name: string) => void }) {
  const [name, setName] = createSignal("");
  return (
    <form
      class="tov-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (name()) props.onCreate(name());
        setName("");
      }}
    >
      <Field label="Name" grow>
        <input
          class="tov-input"
          value={name()}
          placeholder="e.g. Faro Brand Voice"
          onInput={(event) => setName(event.currentTarget.value)}
        />
      </Field>
      <button type="submit" class="btn btn-primary">
        Add profile
      </button>
    </form>
  );
}

function CreateVersion(props: {
  onCreate: (label: string, summary: string) => void;
}) {
  const [label, setLabel] = createSignal("");
  const [summary, setSummary] = createSignal("");
  return (
    <form
      class="tov-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (label() && summary()) props.onCreate(label(), summary());
        setLabel("");
        setSummary("");
      }}
    >
      <Field label="Label">
        <input
          class="tov-input"
          value={label()}
          placeholder="v2"
          onInput={(event) => setLabel(event.currentTarget.value)}
        />
      </Field>
      <Field label="Summary" grow>
        <input
          class="tov-input"
          value={summary()}
          placeholder="What changed in this version"
          onInput={(event) => setSummary(event.currentTarget.value)}
        />
      </Field>
      <button type="submit" class="btn btn-primary">
        Add draft
      </button>
    </form>
  );
}
